import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Grid, Stack, Button, Checkbox } from "@mui/material";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { getIsConsumerOrPersonalApplication } from "@/constants";
import {
  applicationSelector,
  confirmCustomerData,
  entitySelector,
  removeCustomer,
  sendCustomerDetailsLinkToCustomer,
  userDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  UserDetailsState,
  Entity,
  Customer,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { customerSelector } from "@/store/slices/customerSlice";
import type { CustomerSlice, ContactData } from "@/store/slices/types/customerSlice";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
// @ts-expect-error: ignoring as it is existing js file
import Identity from "./Identity";
// @ts-expect-error: ignoring as it is existing js file
import CreditProfile from "./CreditProfile";
// @ts-expect-error: ignoring as it is existing js file
import Employer from "./Employer";
// @ts-expect-error: ignoring as it is existing js file
import AddEmployer from "./AddEmployer";
// @ts-expect-error: ignoring as it is existing js file
import CustomerContactDetails from "./CustomerContactDetails";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPersonalDetails from "./CustomerPersonalDetails";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPersonalProfile from "./CustomerPersonalProfile";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPersonalFinances from "./CustomerPersonalFinances";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPrivacyAgreement from "./CustomerPrivacyAgreement";
// @ts-expect-error: ignoring as it is existing js file
import CustomerFinancialStatement from "./CustomerFinancialStatement";
// @ts-expect-error: ignoring as it is existing js file
import CustomerOtherDocuments from "./CustomerOtherDocuments";
import { CustomerInviteActions } from "./CustomerInviteActions";
import { EmploymentIncomeVerification } from "./EmploymentIncomeVerification";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "../Utils/helperFunction";

interface CustomerDetailsAlternativeLayoutProps {
  customer: Customer;
  applicationId: string;
  applicationType: ApplicationType;
  index: number;
  fieldErrors: Record<string, string>[];
  setFieldErrors: (errors: Record<string, string>[] | Record<string, string>) => void;
  isAcknowledgementAccept?: boolean;
  setIsAcknowledgementAccept?: (isAcknowledgementAccept: boolean) => void;
}

const CustomerDetailsAlternativeLayout = React.memo(
  ({
    customer,
    applicationId,
    applicationType,
    index,
    fieldErrors,
    setFieldErrors,
    setIsAcknowledgementAccept,
    isAcknowledgementAccept,
  }: CustomerDetailsAlternativeLayoutProps) => {
    const dispatch = useDispatch();
    const entity = useSelector<ApplicationFormSlice, Entity[]>(entitySelector);
    const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
    const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);
    const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
    const { isCustomerLogin } = useSelector<CustomerSlice, ContactData>(customerSelector);
    const { confirmCustomerDataLoader } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
    const { accessComponents } = useAccessAppComponent();
    const {
      application: {
        customer: { isEquifaxConsumerReportEnabled, isBankStatementsEnabled },
      },
    } = accessComponents;
    const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;
    const servicePermission = permissions?.services;
    const customerId = customer?._id;
    const [confirmRemoveCustomer, setConfirmRemoveCustomer] = useState<Record<string, boolean>>({});

    const handle = {
      sendFormLinkCustomer: ({
        customerId,
        applicationId,
        applicationType,
      }: {
        customerId: string;
        applicationId: string;
        applicationType: ApplicationType;
      }) => {
        dispatch(sendCustomerDetailsLinkToCustomer({ customerId, applicationId, applicationType }));
      },
      confirmCustomerChanges: () => {
        // show btn invite customer button (linkSend false, customerSave false)
        // show btn if link send (linkSend true, customerSave false)
        // show confirm btn (linkSend true, customerSave true)
        // If broker want to resend then make all state as default (linkSend false, customerSave false)
        dispatch(
          confirmCustomerData({
            customerId: customer._id,
            data: {
              application: applicationId,
              isCustomerDetailsSaved: false, // If broker want to send link again
              isDetailsLinkSend: false, // If broker want to send link again
            },
          }),
        );
      },
    };

    const isSendLinkBtnDisable =
      !customer?.firstName ||
      !customer?.dateOfBirth ||
      customer?.emails?.length <= 0 ||
      customer?.phoneNumbers?.length <= 0 ||
      isDisableEdit;

    const { role, appointmentDate, shares, employers } = customer;
    const personalDetailsKey = applicationId + "-" + index;
    return (
      <Grid
        container
        item
        xs={12}
        style={{
          padding: "0 0 30px",
        }}
        id="customer-section"
      >
        <Grid item xs={12} md={2}>
          <Stack direction="row" style={{ marginBottom: "20px" }}>
            <PersonIcon style={{ marginRight: "15px", color: "#1d4164" }} />
            <Typography fontWeight={600}>
              {getCustomerRole(application.applicationType, application.entities, application.purpose)} #{index + 1}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {role && (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Role:</strong> <br />
                {role}
              </Typography>
            )}
            {appointmentDate && (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Appointment date:</strong> <br />
                {appointmentDate}
              </Typography>
            )}
            {shares && entity?.[0]?.shares?.[0]?.numberOfShares && (
              <Typography style={{ fontSize: "14px" }}>
                <strong>Shares:</strong> <br />
                {shares}/{entity?.[0]?.shares?.[0]?.numberOfShares} (
                {((shares / entity?.[0]?.shares?.[0]?.numberOfShares) * 100).toFixed(2)}
                )%
              </Typography>
            )}
          </Stack>
          {!isCustomerLogin && !confirmRemoveCustomer[customer._id] && (
            <Button
              variant="outlined"
              size="small"
              disabled={isDisableEdit}
              style={{
                padding: 1,
                margin: "10px 5px 0 0",
              }}
              onClick={() => setConfirmRemoveCustomer({ [customer._id]: true })}
            >
              Remove
            </Button>
          )}

          {customer?.isDetailsLinkSend && customer?.isCustomerDetailsSaved && (
            <Button
              variant="outlined"
              size="small"
              style={{
                padding: 1,
                margin: "10px 0 0 0",
              }}
              disabled={isSendLinkBtnDisable || confirmCustomerDataLoader}
              onClick={handle.confirmCustomerChanges}
            >
              {confirmCustomerDataLoader ? "Saving..." : "Confirm"}
            </Button>
          )}

          {confirmRemoveCustomer[customer._id] && (
            <>
              <Typography>Are you sure?</Typography>
              <Button
                size="small"
                variant="text"
                style={{
                  padding: 0,
                }}
                onClick={() => setConfirmRemoveCustomer({ [customer._id]: false })}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="text"
                style={{
                  padding: 0,
                  color: "#d32f2f",
                }}
                onClick={() => {
                  dispatch(
                    removeCustomer({
                      customerId: customer._id,
                      applicationId,
                    }),
                  );
                  setConfirmRemoveCustomer({ [customer._id]: false });
                  setFieldErrors({
                    customerTitle: "",
                    firstName: "",
                    lastName: "",
                    IDNumber: "",
                    dateOfBirth: "",
                    IDCardNumber: "",
                    phoneNumber: "",
                    emailAddress: "",
                    address: "",
                  });
                }}
              >
                Remove
              </Button>
            </>
          )}

          {!isCustomerLogin && customer?.isDetailsLinkSend && (
            <Button
              variant="outlined"
              size="small"
              style={{
                padding: 1,
                margin: "10px 0 0 0",
              }}
              disabled={isSendLinkBtnDisable}
              onClick={() =>
                handle.sendFormLinkCustomer({
                  customerId: customer._id,
                  applicationId,
                  applicationType,
                })
              }
            >
              Resend link
            </Button>
          )}

          <CustomerInviteActions
            customer={customer}
            applicationId={applicationId}
            applicationType={applicationType}
            isCustomerLogin={!!isCustomerLogin}
            isSendLinkBtnDisable={isSendLinkBtnDisable}
            isDisableEdit={isDisableEdit}
          />
        </Grid>

        <Grid container item xs={12} md={10} style={{ padding: "0 0 0 10px" }}>
          {/* Personal Details */}
          <CustomerPersonalDetails
            customer={customer}
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            applicationId={applicationId}
            key={personalDetailsKey}
            isDisableEdit={isDisableEdit}
          />
          {/* Contact Details */}
          <CustomerContactDetails
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            customer={customer}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Personal Profile */}
          <CustomerPersonalProfile
            customer={customer}
            customerId={customerId}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Privacy Agreement */}
          {customer && !isCustomerLogin && (
            <CustomerPrivacyAgreement
              customer={customer}
              applicationId={applicationId}
              applicationType={application?.applicationType}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Credit Profile */}
          {customer && servicePermission?.equifax_basic_report && !isCustomerLogin && (
            <CreditProfile
              customer={customer}
              applicationId={applicationId}
              isDisableEdit={isDisableEdit}
              customerIndex={index}
            />
          )}
          {/* Identity */}
          {customer && isEquifaxConsumerReportEnabled && servicePermission?.id_verification && !isCustomerLogin && (
            <Identity customer={customer} applicationId={applicationId} isDisableEdit={isDisableEdit} />
          )}
          {/* Employer */}
          {isConsumerOrPersonalApplication &&
            employers?.map((employer: Record<string, string>, i: number) => (
              <Employer
                isDisableEdit={isDisableEdit}
                key={employer.id}
                customer={customer}
                employer={employer}
                index={i}
                customerId={customerId}
                setFieldErrors={setFieldErrors}
                applicationId={applicationId}
                customerIndex={index}
              />
            ))}
          {/* Add Employer */}
          {isConsumerOrPersonalApplication && (
            // (!customer?.employers?.[0] || timeOfEmploymentTotal < 12) &&
            <AddEmployer
              customerId={customer._id}
              applicationId={applicationId}
              numberOfEmployers={customer?.employers?.length}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Employment & Income Verification */}
          {customer && isConsumerOrPersonalApplication && !isCustomerLogin && (
            <EmploymentIncomeVerification
              customer={customer}
              applicationId={applicationId}
              isDisableEdit={isDisableEdit}
            />
          )}
          {/* Personal Finances */}
          <CustomerPersonalFinances
            customer={customer}
            customerId={customerId}
            customerIndex={index}
            applicationType={applicationType}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          {/* Bank Statement */}
          {customer && isBankStatementsEnabled && servicePermission?.bank_statements && !isCustomerLogin && (
            <Grid
              item
              xs={12}
              style={{
                margin: "0 0 30px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                paddingBottom: "30px",
              }}
            >
              {/* Customer Financial Statement */}
              <CustomerFinancialStatement
                customer={customer}
                applicationId={applicationId}
                isDisableEdit={isDisableEdit}
              />
            </Grid>
          )}
          {/* Customer Other Documents */}
          <CustomerOtherDocuments customer={customer} applicationId={applicationId} />
          {isCustomerLogin && setIsAcknowledgementAccept && (
            <Stack sx={{ flexDirection: "row", alignItems: "start" }}>
              <Checkbox
                value={isAcknowledgementAccept}
                onChange={(event) => setIsAcknowledgementAccept(event.target.checked)}
              />
              <Typography sx={{ fontSize: "0.80rem", marginBottom: "50px", paddingTop: "7px" }}>
                {(customer?.applications as ApplicationState[])?.[0]?.customer_acknowledgement?.acknowledgement_text}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>
    );
  },
);

export { CustomerDetailsAlternativeLayout };
